import { Component, Element, Host, Prop, getAssetPath, h } from '@stencil/core';

import { LandingStepByStep } from '../../../interfaces/interface.landing';
import { trackLandingStartMyOffer } from '../../../tracking/tracking.landing';
import { DynamicEntryInstallationMode } from '../../../utils/utils.dynamic-entry';
import { DynamicEntryWorkflow, LANDING_FAQ, LANDING_LEGAL, getCityScapeCanvas, scrollTo } from '../../../utils/utils.landing';
import KbbicoLogo from '../../ui-common/svg/kbbico-logo/kbbico-logo';

const MAIN_IMAGE = 'assets/main-image-kbb.svg';
@Component({
  tag: 'landing-unassigned',
  styleUrl: 'landing-unassigned.css',
  assetsDirs: ['assets'],
})
export class LandingUnassigned {
  @Prop() dynamicEntryWorkflow: DynamicEntryWorkflow = DynamicEntryWorkflow.Unassigned;
  @Prop() tabList: string[] = ['vin', 'plate'];
  @Prop() enableMakeModel: boolean = true;
  @Prop() defaultSelectedTab: string = 'vin';
  @Prop() installationMode?: DynamicEntryInstallationMode = DynamicEntryInstallationMode.Redirect;
  @Prop() isCombined: boolean = false;
  @Prop() showLandingContact = true;
  @Prop() vehicle: string;

  @Prop() heading = '';
  @Prop() subheading = '';
  @Prop() inputVin: string = '';
  @Prop() mileage?: number;
  @Prop() theme: string = '';

  @Prop() videoComponent = {
    heading: '',
    subheading: '',
    videoUrl: '',
    thumbnail: '',
  };
  @Prop() stepByStep: LandingStepByStep = {} as LandingStepByStep;
  @Prop() contactUsComponent = {};
  @Prop() faqTitle = '';
  @Prop() faqData = '';
  @Prop() legalContent = '';
  @Prop() showCityscape: boolean = true;

  @Element() el: HTMLElement;
  onClickStartMyOffer = (component: string) => () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    trackLandingStartMyOffer(component);

    const vinInput = this.el.querySelector('input');
    vinInput.focus({ preventScroll: true });
  };

  render() {
    return (
      <Host>
        <div class={`${this.theme}-theme`}>
          {this.renderMainContent()}
          {this.renderMiddleContent()}
          {this.renderFooter()}
        </div>
      </Host>
    );
  }

  renderMainContent() {
    const classList = {
      'mt-8 md:mt-16': this.showCityscape,
    };
    const mainContentClass = {
      'pt-8 md:pt-16': !this.showCityscape,
      'grid grid-cols-12 gap-x-4 lg:gap-x-6 mx-4 lg:mx-0': true,
    };
    const CITYSCAPE_CANVAS_TAG = getCityScapeCanvas();
    return (
      <div class={classList}>
        <landing-background>
          {this.showCityscape && <CITYSCAPE_CANVAS_TAG slot="background" />}
          <div class={mainContentClass}>
            {this.renderHeadingAndDynamicEntry()}
            {this.renderMainImage()}
          </div>
          <div class="grid grid-cols-12 gap-x-4 lg:gap-x-6 mx-4 lg:mx-0 mt-10 md:mt-20">{this.renderVideo()}</div>
        </landing-background>
      </div>
    );
  }

  renderHeadingAndDynamicEntry() {
    return (
      <div class="col-start-1 col-end-13 lg:col-start-2 lg:col-end-7 justify-self-center lg:justify-self-auto">
        <div class="landing-unassigned-heading-text">
          <div class="flex h-14 items-center gap-2">
            <div class="w-48 landing-unassigned-logo-background landing-unassigned-logo-background--visibility">
              <KbbicoLogo />
            </div>
            {this.isCombined && <svg-icon icon="kbb-values-logo" class="w-44" />}
          </div>
          <h1 class="font-extrabold text-5xl leading-none md:text-6xl whitespace-pre-wrap md:mt-4 md:w-screen md:max-w-xl" data-testid="ci-heading">
            {this.heading}
          </h1>
          <h2 class="font-medium text-xl mt-6 md:mt-2 max-w-540" data-testid="ci-sub-heading">
            {this.subheading}
          </h2>
        </div>
        <div class="mt-6 max-w-lg">
          <ico-dynamic-entry-hydrator
            offerCode=""
            tabList={this.tabList}
            enableMakeModel={this.enableMakeModel}
            defaultSelectedTab={this.defaultSelectedTab}
            installationMode={this.installationMode}
            enableTermsConditions={true}
            dynamicEntryWorkflow={this.dynamicEntryWorkflow}
            vehicle={this.vehicle}
            inputVin={this.inputVin}
            mileage={this.mileage}>
            <ico-dynamic-entry whenTermsConditionsClicked={() => scrollTo(LANDING_LEGAL)} />
          </ico-dynamic-entry-hydrator>
        </div>
      </div>
    );
  }

  renderMainImage() {
    return (
      <div class="col-start-1 col-end-13 lg:col-start-7 lg:col-end-12 mt-10 lg:mt-auto mb-8 justify-self-center md:ml-8">
        <img src={getAssetPath(MAIN_IMAGE)} alt="main-image" class="w-full max-w-540" />
      </div>
    );
  }

  renderVideo() {
    return (
      <div class="mt-0 lg:mt-24 pb-16 md:pb-20 col-start-1 col-end-13 lg:col-start-4 lg:col-end-10">
        <div class="text-center mb-4">
          <h2 class="landing-unassigned-heading-text text-2xl font-extrabold">{this.videoComponent.heading}</h2>
          <h3 class="landing-unassigned-sub-heading-text text-sm font-medium">{this.videoComponent.subheading}</h3>
        </div>
        <div class="lg:mx-auto">
          <landing-video {...this.videoComponent} />
        </div>
        <div class="w-48 mt-6 mx-auto">
          <ico-button label="Start My Offer" buttonSize="large" narrow whenClicked={this.onClickStartMyOffer('video')} />
        </div>
      </div>
    );
  }

  renderMiddleContent() {
    return (
      <landing-background white={true}>
        <div class="grid grid-cols-12 gap-x-4 lg:gap-x-6 mx-4 lg:mx-0">
          <div class="mt-14 lg:mt-24 pb-16 md:pb-20 col-start-1 col-end-13 lg:col-start-3 lg:col-end-11">
            {this.renderSteps()}
            {this.renderFAQ()}
          </div>
        </div>
      </landing-background>
    );
  }

  renderSteps() {
    if (this.stepByStep) {
      return (
        <div class="lg:mb-2">
          <landing-step-by-step mainTitle={this.stepByStep.title} mainSubTitle={this.stepByStep.subTitle} steps={this.stepByStep.steps} />
          <div class="w-48 mt-12 mx-auto">
            <ico-button data-testid="bi-btn-step-by-step-start-my-offer" label="Start My Offer" buttonSize="large" narrow whenClicked={this.onClickStartMyOffer('step-by-step')} />
          </div>
          <h5 class="landing-unassigned-base-text mt-4 lg:mt-6 text-xxs font-normal text-center">*Dealer based on location, trade vehicle and/or next vehicle</h5>
        </div>
      );
    }
  }

  renderFAQ() {
    return (
      <div class="mt-16 lg:mt-36">
        <div class="lg:mx-auto">
          <landing-faq faqTitle={this.faqTitle} faqData={this.faqData} />
        </div>
        <div class="w-48 mt-6 mx-auto">
          <ico-button data-testid="bi-btn-faq-start-my-offer" label="Start My Offer" buttonSize="large" narrow whenClicked={this.onClickStartMyOffer('faq')} />
        </div>
      </div>
    );
  }

  renderFooter() {
    return (
      <landing-background extendToBottom={true}>
        <div class="grid grid-cols-12 gap-x-4 lg:gap-x-6 mx-4 lg:mx-0">
          {this.showLandingContact && (
            <div class="mt-20 lg:mt-7 mb-6 md:mb-8 col-start-1 col-end-13 lg:col-start-2 lg:col-end-12">
              <landing-contact {...this.contactUsComponent} whenFaqClicked={() => scrollTo(LANDING_FAQ)} />
            </div>
          )}
          <div class="mb-6 md:mb-10 col-start-1 col-end-13 lg:mx-28 pt-4">
            <landing-legal testId="ci-legal-text" content={this.legalContent}></landing-legal>
          </div>
        </div>
      </landing-background>
    );
  }
}
